import { useEffect, useRef, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ModalBasicLayout from "../ModalBasicLayout";
import { useTranslationApp } from "../../../lib/i18next";
import IconButton from "@mui/material/IconButton";
import MUIButton from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { setShowModalSelectSubscription } from "../../../actions/ui";
import { getPathDatabaseByRole, startLogout } from "../../../actions/auth";
import {
  startGetIsActivePlan,
  startGetPayments,
  startGetPayInvoiceUrl,
  startCancelReason,
  startCancelSubscription,
  setIsPlanActiveResolved,
} from "../../../actions/payments";
import {
  usePlanType,
  getModalAriaLabel,
} from "../../Pricing/plansConfiguration";
import Pricing from "../../Pricing/Pricing";
import {
  MODAL_PAY_SUBSCRIPTION_PENDING_PAYMENT_TITLE,
  MODAL_INVOICE_SUBSCRIPTION,
  BUTTON_YOU_HAVE_DOUBTS,
  MODAL_CANCEL_SUBSCRIPTION_TITLE,
  ALERT_CANCEL_SUBSCRIPTION_TITLE,
  ALERT_CANCEL_SUBSCRIPTION_DESCRIPTION,
  ALERT_CANCEL_PLAN_TITLE,
  BUTTON_GO_TO_BILLING,
  LOGOUT,
  PAYWALL_INSUFFICIENT_CREDITS_ARIA_LABEL,
} from "../../../i18n/keysTranslations";
import { capitalizeFirstLetter } from "../../../utils/string";
import addMonths from "date-fns/addMonths";
import {
  RECURRENT_MULTIPLIER,
  companyInformation,
  LINK_LANDING_CONTACT,
  ALERT_ICON_TYPE_SUCCESS,
  SUBSCRIPTION_PAYWALL_REASONS,
} from "../../../utils/constants";
import { getLastPendingPackagePlanInvoiceEntry } from "../../../utils/pendingPackagePlanInvoice";
import Button from "../../Buttons/Button";
import Link from "@mui/material/Link";
import CircularProgress from "@mui/material/CircularProgress";
import ModalCancelSubscription from "../ModalCancelSubscription";
import { SimpleAlert } from "../../Alerts/Alerts";
import { isFormPath } from "../../../router/formRoutes";

const getSubscriptionName = (sub) => {
  if (sub === "specialist") return "Add-On Marketing Specialist";
  return capitalizeFirstLetter(sub);
};

const getInvoiceConcept = (data, t) => {
  const items = data?.items || [];
  const paidDate = data?.paidDate ? data?.paidDate : new Date().getTime();
  const recurrent = data?.params?.recurrent || "monthly";
  const period = RECURRENT_MULTIPLIER?.[recurrent] || 1;
  const startDate = new Date(paidDate);
  const endDate = new Date(addMonths(paidDate, period));
  const sub = items[0]?.subscription || data?.subscription;
  const name = getSubscriptionName(sub);
  return t(MODAL_INVOICE_SUBSCRIPTION, {
    name,
    startDate,
    endDate,
  });
};

const SHOW_CANCELLATION_OPTION = false;

const CompanyFooter = ({
  t,
  onOpenCancellationModal,
  isCancellingSubscription,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    }}
  >
    <Link
      href={LINK_LANDING_CONTACT}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{
        mt: 2,
        textDecoration: "underline",
        color: "text.secondary",
        cursor: "pointer",
        fontSize: "0.875rem",
        "&:hover": {
          color: "primary.main",
        },
      }}
    >
      {t(BUTTON_YOU_HAVE_DOUBTS)}
    </Link>
    {SHOW_CANCELLATION_OPTION ? (
      <Box
        sx={{
          mt: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Link
          component="button"
          type="button"
          onClick={onOpenCancellationModal}
          underline="hover"
          sx={{
            textDecoration: "underline",
            color: "text.secondary",
            cursor: "pointer",
            fontSize: "0.875rem",
            "&:hover": {
              color: "primary.main",
            },
          }}
        >
          {t(MODAL_CANCEL_SUBSCRIPTION_TITLE)}
        </Link>
        {isCancellingSubscription && (
          <CircularProgress size={16} color="inherit" />
        )}
      </Box>
    ) : null}
    <Box
      sx={{
        mt: 1,
        textAlign: "center",
        borderTop: 1,
        borderColor: "divider",
        width: "100%",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          display: "block",
          lineHeight: 1.6,
        }}
      >
        {companyInformation.name} - A Delaware LLC
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          display: "block",
          lineHeight: 1.6,
        }}
      >
        {companyInformation.address}
      </Typography>
    </Box>
  </Box>
);

const PendingPaymentContent = ({
  pendingInvoice,
  onOpenCancellationModal,
  isCancellingSubscription,
}) => {
  const { t } = useTranslationApp();
  const navigate = useNavigate();
  const dispatch = useDispatchApp();

  const onGoToBilling = () => {
    dispatch(setShowModalSelectSubscription(false));
    navigate("/billing");
  };
  return (
    <Box
      sx={{
        p: 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: 700,
          color: "text.primary",
        }}
      >
        {t(MODAL_PAY_SUBSCRIPTION_PENDING_PAYMENT_TITLE, {
          value: pendingInvoice?.value,
          currency: pendingInvoice?.currency || "USD",
        })}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          textAlign: "center",
          color: "text.secondary",
          maxWidth: 480,
        }}
      >
        {getInvoiceConcept(pendingInvoice, t)}
      </Typography>

      <Button
        variant="contained"
        color="primary"
        onClick={onGoToBilling}
        sx={{
          mt: 1,
          px: 4,
          py: 1.5,
          fontSize: "1rem",
          fontWeight: 600,
        }}
      >
        {t(BUTTON_GO_TO_BILLING)}
      </Button>

      <CompanyFooter
        t={t}
        onOpenCancellationModal={onOpenCancellationModal}
        isCancellingSubscription={isCancellingSubscription}
      />
    </Box>
  );
};

const ModalPaySubscription = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isBillingPage = location.pathname === "/billing";
  const isFormRoute = isFormPath(location.pathname);

  const { isPlanActive, isPlanActiveResolved, payments, subscriptionPlan } =
    useSelectorApp((state) => state.payments);
  const modalOpen = useSelectorApp(
    (state) => state.ui.showModalSelectSubscription
  );
  const paywallReason = useSelectorApp(
    (state) => state.ui.modalSelectSubscriptionReason
  );
  const [paymentsFetched, setPaymentsFetched] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] =
    useState(false);

  const refContainer = useRef();
  const modalOpenRef = useRef(modalOpen);
  const prevPathnameForPlanCheckRef = useRef(null);
  const hasCompletedInitialPlanCheckRef = useRef(false);
  const theme = useTheme();

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const { currentTypeOfPlan } = usePlanType();

  const { userID } = dispatch(getPathDatabaseByRole());

  const pendingInvoice = useMemo(() => {
    const entry = getLastPendingPackagePlanInvoiceEntry(payments);
    return entry?.invoiceData ?? null;
  }, [payments]);
  const isSpecialPendingInvoiceMode =
    !isPlanActive && modalOpen && pendingInvoice !== null;
  const isInsufficientCreditsPaywall =
    paywallReason === SUBSCRIPTION_PAYWALL_REASONS.INSUFFICIENT_CREDITS &&
    isPlanActive &&
    !isSpecialPendingInvoiceMode;

  const onVerifyPlanActive = async () => {
    try {
      const planActiveResult = await dispatch(startGetIsActivePlan());
      if (planActiveResult === null) {
        return null;
      }
      if (!isBillingPage) {
        dispatch(setShowModalSelectSubscription(planActiveResult === false));
      }
      return planActiveResult;
    } finally {
      hasCompletedInitialPlanCheckRef.current = true;
    }
  };

  const onCloseModal = () => {
    dispatch(setShowModalSelectSubscription(false));
  };

  const onLogout = () => {
    dispatch(startLogout());
  };

  useEffect(() => {
    if (!userID) {
      hasCompletedInitialPlanCheckRef.current = false;
      prevPathnameForPlanCheckRef.current = null;
      return;
    }
    dispatch(setIsPlanActiveResolved(false));
    onVerifyPlanActive();
    //eslint-disable-next-line
  }, [userID]);

  useEffect(() => {
    modalOpenRef.current = modalOpen;
  }, [modalOpen]);

  useEffect(() => {
    if (!isPlanActive) return;
    dispatch(setShowModalSelectSubscription(false));
  }, [isPlanActive, dispatch]);

  useEffect(() => {
    if (!userID) return;

    const previousPath = prevPathnameForPlanCheckRef.current;

    if (previousPath === null) {
      prevPathnameForPlanCheckRef.current = location.pathname;
      return;
    }

    if (previousPath === location.pathname) {
      return;
    }

    prevPathnameForPlanCheckRef.current = location.pathname;

    if (!hasCompletedInitialPlanCheckRef.current) {
      return;
    }

    if (location.pathname === "/billing") return;

    if (isPlanActiveResolved && !isPlanActive) {
      if (!modalOpenRef.current) {
        dispatch(setShowModalSelectSubscription(true));
      }
    } else if (isPlanActive) {
      dispatch(setShowModalSelectSubscription(false));
    }
    //eslint-disable-next-line
  }, [location.pathname, userID, isPlanActive, isPlanActiveResolved]);

  useEffect(() => {
    if (!modalOpen || !userID || isPlanActive) return;
    setPaymentsFetched(false);
    dispatch(
      startGetPayments({
        onCallbackFinish: () => setPaymentsFetched(true),
      })
    );
  }, [modalOpen, userID, isPlanActive, dispatch]);

  useEffect(() => {
    if (
      !isSpecialPendingInvoiceMode ||
      !paymentsFetched ||
      !pendingInvoice?.paymentID
    )
      return;
    dispatch(
      startGetPayInvoiceUrl({
        paymentIntent: pendingInvoice.paymentID,
        coldBoot: true,
      })
    );
  }, [
    isSpecialPendingInvoiceMode,
    paymentsFetched,
    pendingInvoice?.paymentID,
    dispatch,
  ]);

  const onOpenCancellationModal = () => {
    setShowCancellationModal(true);
  };

  const onCloseCancellationModal = () => {
    setShowCancellationModal(false);
  };

  const onCancelSubscriptionSubmit = async (cancellationData) => {
    setShowCancellationModal(false);
    setIsCancellingSubscription(true);
    const response = await dispatch(startCancelSubscription());
    if (response?.ok) {
      SimpleAlert({
        title: t(
          !subscriptionPlan
            ? ALERT_CANCEL_PLAN_TITLE
            : ALERT_CANCEL_SUBSCRIPTION_TITLE
        ),
        text: !subscriptionPlan
          ? null
          : t(ALERT_CANCEL_SUBSCRIPTION_DESCRIPTION, {
              date: response?.cancelDateTime
                ? new Date(response?.cancelDateTime)
                : new Date(),
            }),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      dispatch(startCancelReason(cancellationData));
      dispatch(setShowModalSelectSubscription(false));
      navigate("/billing");
    }
    setIsCancellingSubscription(false);
  };

  if (!modalOpen || !userID || isFormRoute || !isPlanActiveResolved)
    return null;

  const canClose = isBillingPage || isPlanActive;
  const showCloseButton = isBillingPage || isPlanActive;

  if (!isPlanActive && !paymentsFetched) return null;

  if (isInsufficientCreditsPaywall) {
    return (
      <ModalBasicLayout
        modalOpen={true}
        onCloseModal={onCloseModal}
        fullWidth
        scroll="paper"
        hasTitle={false}
        sxContent={{ p: 0, px: 0 }}
        sxPaper={{ zIndex: 15300, maxWidth: 1080 }}
        ariaLabel={t(PAYWALL_INSUFFICIENT_CREDITS_ARIA_LABEL)}
      >
        <Pricing
          variant="insufficientCredits"
          onCallbackFinish={onCloseModal}
          onCloseModal={onCloseModal}
        />
      </ModalBasicLayout>
    );
  }

  return (
    <>
      <ModalBasicLayout
        modalOpen={true}
        onCloseModal={canClose ? onCloseModal : () => {}}
        fullWidth
        scroll="paper"
        ariaLabel={getModalAriaLabel(currentTypeOfPlan, t)}
        sxContent={{ p: 0, px: 0 }}
        hasTitle={false}
        refContent={refContainer}
        sxPaper={{
          zIndex: 15300,
          maxWidth: isSpecialPendingInvoiceMode && paymentsFetched ? 640 : 1280,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: { xs: "10px", sm: "15px" },
            top: { xs: "10px", sm: "15px" },
            zIndex: 1000,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          {!isPlanActive && !isBillingPage && (
            <MUIButton
              variant="text"
              size="small"
              onClick={onLogout}
              sx={{
                color: "text.primary",
                textTransform: "none",
                minWidth: "auto",
                px: "15px !important",
                py: 0.5,
                fontWeight: 500,
                fontSize: "14px",
                "& .MuiButton-startIcon": { mr: 0.75 },
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              {t(LOGOUT)}
            </MUIButton>
          )}
          {showCloseButton && (
            <IconButton
              aria-label="close"
              onClick={onCloseModal}
              sx={{
                flexShrink: 0,
                p: 0.5,
                fontSize: "1.4rem",
                color: "background.paperSecondaryContrastText",
                backgroundColor: "background.paperSecondary",
                "&:hover": {
                  color: "background.paperSecondaryContrastText",
                  backgroundColor: "background.paperSecondary",
                  filter: theme.palette.hover.main,
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 28 }} />
            </IconButton>
          )}
        </Box>
        {isSpecialPendingInvoiceMode && paymentsFetched ? (
          <PendingPaymentContent
            t={t}
            pendingInvoice={pendingInvoice}
            onOpenCancellationModal={onOpenCancellationModal}
            isCancellingSubscription={isCancellingSubscription}
          />
        ) : (
          <Pricing
            onCallbackFinish={() => {
              onCloseModal();
            }}
            onCloseModal={onCloseModal}
          />
        )}
      </ModalBasicLayout>
      {showCancellationModal && (
        <ModalCancelSubscription
          modalOpen={showCancellationModal}
          onCloseModal={onCloseCancellationModal}
          onConfirmCancellation={onCancelSubscriptionSubmit}
          loading={isCancellingSubscription}
        />
      )}
    </>
  );
};

export default ModalPaySubscription;
